import {
  DatabaseAuto,
  IdataArrayResponse,
  IfilterProps,
  IvendorBillPayout,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

/**
 * Represents a vendor bill payout in the system.
 * This class provides methods to manage and track payouts for vendor bills,
 * including retrieval, updating, and filtering of payout records.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link VendorBillPayout} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const vendorBillPayout = await VendorBillPayout.getAll();
 *
 * const vendorBillPayout = await VendorBillPayout.filterAll({
 *
 * });
 *
 * const vendorBillPayout = await VendorBillPayout.bulkDelete({
 *
 * });
 *
 * const vendorBillPayout = await VendorBillPayout.getOne('resourceId');
 *
 * const vendorBillPayout = await VendorBillPayout.remove();
 *
 * ```
 */
export class VendorBillPayout extends DatabaseAuto {
  /** Base API endpoint for vendor bill payout operations */
  static readonly baseUrl = '/vendor-bill-payout';

  /** Unique reference ID for the vendor bill payout */
  readonly urId: IvendorBillPayout['urId'];

  /** The vendor bill associated with this payout */
  readonly vendorBill: IvendorBillPayout['vendorBill'];

  /** Total amount of the payout including all charges */
  readonly total: IvendorBillPayout['total'];

  /** Subtotal amount before any additional charges */
  readonly subTotal: IvendorBillPayout['subTotal'];

  /** Date when the payout was initiated */
  readonly dateInitiated: IvendorBillPayout['dateInitiated'];

  /** Date when the payout was completed */
  readonly dateCompleted: IvendorBillPayout['dateCompleted'];

  /** Current status of the payout */
  readonly status: IvendorBillPayout['status'];

  /** Company associated with the vendor bill payout */
  readonly company: IvendorBillPayout['company'];

  /** Payment metadata */
  readonly paymentMeta?: IvendorBillPayout['paymentMeta'];

  /**
   * Creates a new instance of VendorBillPayout.
   * @param {IvendorBillPayout} data - The vendor bill payout data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: IvendorBillPayout) {
    validateData(data, {
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique reference ID must be a non-empty string'
      }
    });

    super(data);

    this.urId = data.urId;
    this.vendorBill = data.vendorBill;
    this.total = data.total;
    this.subTotal = data.subTotal;
    this.dateInitiated = data.dateInitiated;
    this.dateCompleted = data.dateCompleted;
    this.status = data.status;
    this.company = data.company;
    this.paymentMeta = data.paymentMeta;
  }

  /**
   * Retrieves all vendor bill payouts with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: VendorBillPayout[] }>}
   * An object containing the total count and an array of vendor bill payouts
   * @throws {Error} If there's an error fetching the vendor bill payouts
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();

    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<IvendorBillPayout>>(`${VendorBillPayout.baseUrl}?${query}`);

    const vendorBillPayouts = await lastValueFrom(observer$);

    return {
      count: vendorBillPayouts.count,
      dataArr: vendorBillPayouts.data.map(val => new VendorBillPayout(val))
    };
  }

  /**
   * Filters vendor bill payouts based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: VendorBillPayout[] }>}
   * An object containing the total count and an array of filtered vendor bill payouts
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<IvendorBillPayout>>(
        `${VendorBillPayout.baseUrl}/filter`,
        filter
      );

    const vendorBillPayouts = await lastValueFrom(observer$);

    return {
      count: vendorBillPayouts.count,
      dataArr: vendorBillPayouts.data.map(val => new VendorBillPayout(val))
    };
  }

  /**
   * Retrieves a single vendor bill payout by its ID.
   * @param {string} resourceId - The unique identifier of the vendor bill payout to retrieve
   * @returns {Promise<VendorBillPayout>} The requested vendor bill payout
   * @throws {Error} If the vendor bill payout is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<IvendorBillPayout>(`${VendorBillPayout.baseUrl}/${resourceId}`);
    const vendorBillPayout = await lastValueFrom(observer$);

    return new VendorBillPayout(vendorBillPayout);
  }

  /**
   * Updates the status of the current vendor bill payout.
   * @param {{ status: IvendorBillPayout['status'] }} payload - The new status for the vendor bill payout
   * @returns {Promise<VendorBillPayout>} The updated vendor bill payout
   * @throws {Error} If the update operation fails or the vendor bill payout doesn't exist
   */
  async update(payload: { status: IvendorBillPayout['status'] }) {
    const observer$ = CommerceClient.ehttp
      .makePut<IvendorBillPayout>(
        `${VendorBillPayout.baseUrl}/${this._id}`,
        payload
      );

    return new VendorBillPayout(await lastValueFrom(observer$));
  }
}
